import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Target, 
  Heart, 
  Zap, 
  Shield,
  Cpu,
  CheckCircle,
  Mail,
  MapPin,
  ArrowRight,
  Lock,
  Clock,
  Award,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Accuracy First',
      description: 'We\'re obsessed with providing accurate calculations. Our AI is trained on real construction data and industry standards.',
    },
    {
      icon: Heart,
      title: 'Built for Builders',
      description: 'Whether you\'re a contractor or DIYer, we understand your needs. Our tool is designed by builders, for builders.',
    },
    {
      icon: Zap,
      title: 'Speed Matters',
      description: 'Time is money. Get instant calculations so you can focus on what matters - building great projects.',
    },
    {
      icon: Shield,
      title: 'Privacy Protected',
      description: 'Your project data stays private. We never share or sell your information to third parties.',
    },
  ];

  const stats = [
    { value: '50,000+', label: 'Calculations Made' },
    { value: '10,000+', label: 'Happy Builders' },
    { value: '6', label: 'Material Types' },
    { value: '98%', label: 'Accuracy Rate' },
  ];

  const trustSignals = [
    {
      icon: Lock,
      title: 'Secure & Private',
      description: 'Bank-level encryption protects your data. SOC 2 compliant infrastructure.',
    },
    {
      icon: Clock,
      title: '99.9% Uptime',
      description: 'Reliable service when you need it. Our infrastructure is built for reliability.',
    },
    {
      icon: Award,
      title: 'Industry Standard',
      description: 'Calculations based on RSMeans data and industry-standard formulas.',
    },
    {
      icon: TrendingUp,
      title: 'Always Improving',
      description: 'Our AI learns from millions of data points to improve accuracy continuously.',
    },
  ];

  const technologyFeatures = [
    'Advanced AI trained on construction industry data',
    'Real-time material price awareness',
    'Smart waste factor calculations',
    'Natural language understanding',
    'Instant response times under 2 seconds',
    'Mobile-optimized for on-site use',
  ];

  return (
    <>
      <Helmet>
        <title>About Us - CalcBuildAI</title>
        <meta name="description" content="Learn about CalcBuildAI's mission to help builders calculate materials smarter and reduce waste with AI-powered tools." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Cpu className="w-4 h-4" />
              Powered by Advanced AI
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Smart Estimates for{' '}
              <span className="text-blue-600">Smarter Builds</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              CalcBuildAI combines construction expertise with cutting-edge AI 
              to help builders save time, reduce waste, and make confident material decisions.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-white border-y">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                To eliminate material waste and estimation errors in construction 
                by making professional-grade calculations accessible to everyone.
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Why We Built CalcBuildAI
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Construction material waste costs the industry billions annually. 
                    Over-ordering ties up capital. Under-ordering delays projects.
                  </p>
                  <p className="text-gray-600">
                    We believe AI can solve this. By understanding natural language 
                    project descriptions, CalcBuildAI delivers accurate estimates 
                    in seconds—not hours.
                  </p>
                </div>
                <div className="space-y-4">
                  {['Reduce material waste by up to 15%', 'Save hours on every project', 'Never over-order again'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="font-medium text-gray-900">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Our Story
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="prose prose-lg max-w-none text-gray-600">
                <p>
                  CalcBuildAI started with a simple frustration: estimating materials 
                  for construction projects took too long and often resulted in 
                  buying too much or too little.
                </p>
                <p>
                  After years of working with spreadsheets, reference tables, and 
                  outdated calculators, we asked: what if AI could understand a 
                  project description and calculate materials instantly?
                </p>
                <p>
                  We combined deep construction industry knowledge with modern AI 
                  to create a tool that speaks builder. Just describe your project 
                  like you'd explain it to a colleague, and get professional-grade 
                  estimates in seconds.
                </p>
                <p className="font-medium text-gray-900">
                  Today, CalcBuildAI serves thousands of builders—from weekend 
                  DIYers renovating their first bathroom to professional contractors 
                  managing multi-site projects.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Factors */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Built for Trust
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We take accuracy and security seriously. Here's how we protect 
                your projects and deliver reliable results.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trustSignals.map((signal, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <signal.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{signal.title}</h3>
                  <p className="text-sm text-gray-600">{signal.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="py-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Powered by Advanced Technology
                </h2>
                <p className="text-gray-300 mb-8">
                  Our AI understands construction. It knows that a "10x12 deck" 
                  means 120 square feet, that you'll need joist hangers, and 
                  that a 10% waste factor is standard for lumber projects.
                </p>
                <div className="grid gap-3">
                  {technologyFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-gray-200">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="text-sm text-gray-400 mb-2">Example Input</div>
                <div className="bg-gray-900 rounded-lg p-4 mb-4 text-gray-200 font-mono text-sm">
                  "I need to pour a 20x24 foot concrete patio, 4 inches thick"
                </div>
                <div className="text-sm text-gray-400 mb-2">CalcBuildAI Response</div>
                <div className="bg-gray-900 rounded-lg p-4 text-gray-200 text-sm space-y-2">
                  <div>📦 <strong>5.93 cubic yards</strong> of concrete</div>
                  <div>📦 <strong>6.5 cu yd</strong> with 10% waste</div>
                  <div>💰 <strong>$780-$1,170</strong> for ready-mix</div>
                  <div>📋 Forms, rebar, stakes included</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Our Values
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Build Smarter?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Join thousands of builders who trust CalcBuildAI for accurate estimates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/">
                <Button 
                  size="lg" 
                  variant="light"
                  icon={ArrowRight} 
                  iconPosition="right"
                >
                  Try Free Calculator
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="light-outline"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-blue-100">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span>support@calcbuildai.com</span>
              </div>
              <div className="hidden sm:block">|</div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>San Francisco, CA</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
